import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from '@unform/core';
import { FiX } from 'react-icons/fi';
import { Error } from '../Input/styles';

import { Container, StyleProps, Tag, CustomInput } from './styles';

interface InputTagProps extends StyleProps {
  name: string;
  placeholder?: string;
  onChangeFireCallback?: () => void;
}

const keysAcceptedToEnter = ['Enter', ';'];

const InputTag: React.FC<InputTagProps> = ({
  name,
  placeholder,
  formLook,
  onChangeFireCallback,
}) => {
  const { error, fieldName, registerField, defaultValue, clearError } =
    useField(name);
  const [tags, setTags] = useState<string[]>(
    Array.isArray(defaultValue) ? defaultValue : [],
  );
  const tagsRef = useRef(tags);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const mounted = useRef(false);

  const onBlur = useCallback(() => {
    if (!inputRef.current) return;
    const tag = inputRef.current.value.trim().replace(/;/g, '');
    if (tag) {
      if (!tagsRef.current.includes(tag)) {
        setTags((prev) => [...prev, tag]);
        clearError();
      }
    }
    inputRef.current.value = '';
  }, [clearError]);

  const handleOnKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!inputRef.current) return;

      if (keysAcceptedToEnter.includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        const tag = event.currentTarget.value.trim().replace(/;/g, '');
        if (tag) {
          if (!tagsRef.current.includes(tag)) {
            setTags((prev) => [...prev, tag]);
            clearError();
          }
        }
        inputRef.current.value = '';
      }
    },
    [clearError],
  );

  const preventPropagation = useCallback((event) => {
    if (keysAcceptedToEnter.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => () => {
      setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
      clearError();
    },
    [clearError],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      clearValue: () => setTags([]),
      getValue: () => tagsRef.current,
      setValue: (newTags) => setTags(newTags),
      ref: tagsRef,
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    } else {
      tagsRef.current = tags;
      onChangeFireCallback?.();
    }
  }, [tags, onChangeFireCallback]);

  return (
    <Container isErrored={!!error} formLook={formLook}>
      {tags.map((tag) => (
        <Tag formLook={formLook} key={tag}>
          {tag}
          <button type="button" onClick={handleRemoveTag(tag)}>
            <FiX />
          </button>
        </Tag>
      ))}

      <CustomInput
        type="text"
        placeholder={placeholder}
        formLook={formLook}
        onKeyUp={handleOnKeyUp}
        onKeyPress={preventPropagation}
        onBlur={onBlur}
        ref={inputRef}
      />
      {error && (
        <Error>
          <span dangerouslySetInnerHTML={{ __html: error }} />
        </Error>
      )}
    </Container>
  );
};

export default InputTag;
